import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Globe, User, Mail, MapPin, Facebook, Loader2 } from "lucide-react";
import { BRAND, SERVICES } from "@/data/content";
import { Reveal, Overline } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  motorcycle: "",
  service: "",
  preferred_date: "",
  message: "",
};

const inputCls =
  "w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-cream placeholder-cream/30 font-light transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Booking request sent — we'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Something went wrong. Please try again or call us."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-charcoal py-24 lg:py-36"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Left — details */}
        <div className="lg:col-span-5">
          <Reveal>
            <Overline className="mb-7">Get In Touch</Overline>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream uppercase tracking-tight leading-[0.98]">
              Request A <span className="gold-text italic lowercase">booking.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-cream/55 font-light leading-relaxed max-w-md">
              Tell us about your motorcycle and preferred date. Carlos will
              personally get back to you to confirm your detail.
            </p>
          </Reveal>

          <div className="mt-12 space-y-6">
            <Reveal delay={0.14}>
              <div>
                <span className="font-serif text-2xl text-cream">{BRAND.name}</span>
                <p className="font-mono text-[11px] tracking-[0.2em] text-gold/70 mt-1">
                  {BRAND.tagline}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="flex items-center gap-4 text-cream/80">
                <User size={18} className="text-gold" />
                <span className="font-light">{BRAND.owner}</span>
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <a
                data-testid="contact-phone"
                href={BRAND.phoneHref}
                className="flex items-center gap-4 text-cream/80 hover:text-gold transition-colors"
              >
                <Phone size={18} className="text-gold" />
                <span className="font-light tracking-wide">{BRAND.phone}</span>
              </a>
            </Reveal>
            <Reveal delay={0.24}>
              <a
                data-testid="contact-email"
                href={`mailto:${BRAND.email}`}
                className="flex items-center gap-4 text-cream/80 hover:text-gold transition-colors"
              >
                <Mail size={18} className="text-gold" />
                <span className="font-light tracking-wide">{BRAND.email}</span>
              </a>
            </Reveal>
            <Reveal delay={0.26}>
              <div className="flex items-center gap-4 text-cream/80">
                <Globe size={18} className="text-gold" />
                <span className="font-light">{BRAND.website}</span>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div data-testid="contact-location" className="flex items-center gap-4 text-cream/80">
                <MapPin size={18} className="text-gold" />
                <span className="font-light">{BRAND.location}</span>
              </div>
            </Reveal>
            <Reveal delay={0.34}>
              <a
                data-testid="contact-facebook"
                href={BRAND.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 mt-2 border border-gold/40 hover:border-gold hover:bg-gold/10 transition-all duration-300 px-4 py-2.5 text-cream/80 hover:text-gold"
              >
                <Facebook size={18} className="text-gold" />
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase">
                  Follow on Facebook
                </span>
              </a>
            </Reveal>
          </div>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-7">
          <Reveal y={36}>
            <form
              data-testid="booking-form"
              onSubmit={submit}
              className="bg-panel border border-white/5 p-6 sm:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
                    Name *
                  </label>
                  <input
                    data-testid="booking-input-name"
                    className={inputCls}
                    placeholder="Your name"
                    value={form.name}
                    onChange={set("name")}
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
                    Phone *
                  </label>
                  <input
                    data-testid="booking-input-phone"
                    className={inputCls}
                    placeholder="Your phone"
                    value={form.phone}
                    onChange={set("phone")}
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
                    Email *
                  </label>
                  <input
                    data-testid="booking-input-email"
                    type="email"
                    className={inputCls}
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={set("email")}
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
                    Motorcycle Make / Model
                  </label>
                  <input
                    data-testid="booking-input-motorcycle"
                    className={inputCls}
                    placeholder="e.g. Ducati Monster"
                    value={form.motorcycle}
                    onChange={set("motorcycle")}
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
                    Service Required
                  </label>
                  <select
                    data-testid="booking-input-service"
                    className={`${inputCls} [&>option]:bg-panel [&>option]:text-cream`}
                    value={form.service}
                    onChange={set("service")}
                  >
                    <option value="">Select a service</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
                    Preferred Date
                  </label>
                  <input
                    data-testid="booking-input-date"
                    type="date"
                    className={`${inputCls} [color-scheme:dark]`}
                    value={form.preferred_date}
                    onChange={set("preferred_date")}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
                  Message
                </label>
                <textarea
                  data-testid="booking-input-message"
                  rows={3}
                  className={`${inputCls} resize-none`}
                  placeholder="Anything we should know?"
                  value={form.message}
                  onChange={set("message")}
                />
              </div>

              <button
                data-testid="booking-form-submit-button"
                type="submit"
                disabled={loading}
                className="mt-9 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold text-ink px-10 py-4 font-mono text-xs tracking-[0.22em] uppercase hover:bg-champagne transition-colors duration-300 disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending…" : "Request A Booking"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
